import InputUpload from '@app/components/atoms/Images/InputUpload'
import LabelField from '@app/components/atoms/LabelField/LabelField'
import Section from '@app/components/atoms/Section/Section'
import { FORM_COLOR } from '@app/constant/ColorDefault'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import PartnerEditSchema from './PartnerEditSchema'

const ButtonCustom = styled(Button)({
  marginTop: 20,
  marginRight: 20,
})

const GridCustomizer = styled(Grid)({
  marginBottom: '5%',
})
const TypographyCustomizer = styled(Typography)({
  color: FORM_COLOR.REQUIRED_ERROR,
  marginLeft: '14px !important',
})
const DivCustomizer = styled('div')({
  '& .ck .ck-editor__main > .ck-editor__editable': {
    minHeight: '150px',
    borderBottomLeftRadius: '5px',
    borderBottomRightRadius: '5px',
  },
  '& .ck-rounded-corners .ck.ck-editor__top .ck-sticky-panel .ck-toolbar': {
    borderTopLeftRadius: '5px',
    borderTopRightRadius: '5px',
  },
})

export default function PartnerEdit({ partner }) {
  const { t } = useTranslation()

  const initialValues = {
    ...partner,
    logo: [partner.logo],
    favicon: [partner.favicon],
    slider: partner.images,
  }

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: yupResolver(PartnerEditSchema),
  })

  const onSubmit = (data) => {
    Inertia.put(route('admin.partners.update', partner.id), data, {})
  }
  const onError = () => {}

  return (
    <GridCustomizer container spacing={2}>
      <Grid item xs={12}>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Section title={t('PARTNER.BASIC_INFO')}>
                <Grid item lg={7} md={12} sm={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <LabelField required title={t('PARTNER.NAME')}>
                        <TextField
                          fullWidth
                          name="name"
                          size="small"
                          type="text"
                          variant="outlined"
                          {...register('name')}
                          error={errors.name ? true : false}
                          helperText={errors.name?.message}
                        />
                      </LabelField>
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField required title={t('PARTNER.EMAIL')}>
                        <TextField
                          fullWidth
                          autoComplete="email"
                          name="email"
                          size="small"
                          type="email"
                          variant="outlined"
                          {...register('email')}
                          error={errors.email ? true : false}
                          helperText={errors.email?.message}
                        />
                      </LabelField>
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField required title={t('PARTNER.PHONE')}>
                        <TextField
                          fullWidth
                          name="phone"
                          size="small"
                          type="tel"
                          variant="outlined"
                          {...register('phone')}
                          error={errors.phone ? true : false}
                          helperText={errors.phone?.message}
                        />
                      </LabelField>
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField required title={t('PARTNER.ADDRESS')}>
                        <TextField
                          fullWidth
                          name="address"
                          size="small"
                          type="text"
                          variant="outlined"
                          {...register('address')}
                          error={errors.address ? true : false}
                          helperText={errors.address?.message}
                        />
                      </LabelField>
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField title={t('PARTNER.DESCRIPTION')}>
                        <DivCustomizer>
                          <CKEditor
                            {...register('description')}
                            data={getValues('description')}
                            editor={ClassicEditor}
                            id="description"
                            name="description"
                            onChange={(value, editor) => setValue('description', editor.getData())}
                          />
                        </DivCustomizer>
                      </LabelField>
                    </Grid>
                  </Grid>
                </Grid>
              </Section>
            </Grid>
            <Grid item xs={12}>
              <Section title={t('PARTNER.PORTAL_INFO')}>
                <Grid item lg={7} md={12} sm={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <LabelField title={t('PARTNER.TITLE')}>
                        <TextField
                          fullWidth
                          name="web_title"
                          size="small"
                          type="text"
                          variant="outlined"
                          {...register('web_title')}
                          error={errors.web_title ? true : false}
                          helperText={errors.web_title?.message}
                        />
                      </LabelField>
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField required title={t('PARTNER.LOYALTY_UNIT')}>
                        <TextField
                          fullWidth
                          name="loyalty_unit"
                          size="small"
                          type="text"
                          variant="outlined"
                          {...register('loyalty_unit')}
                          error={errors.loyalty_unit ? true : false}
                          helperText={errors.loyalty_unit?.message}
                        />
                      </LabelField>
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField title={t('PARTNER.HOME_PAGE')}>
                        <TextField
                          fullWidth
                          name="home_page_link"
                          size="small"
                          type="text"
                          variant="outlined"
                          {...register('home_page_link')}
                          error={errors.home_page_link ? true : false}
                          helperText={errors.home_page_link?.message}
                        />
                      </LabelField>
                    </Grid>
                  </Grid>
                </Grid>
              </Section>
            </Grid>

            <Grid item xs={12}>
              <Section title={t('PARTNER.IMAGES')}>
                <Grid item lg={7} md={12} sm={12} xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h5"></Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <LabelField required recommend={t('PARTNER.SLIDER_RECOMMEND')} title={t('PARTNER.LOGO')}>
                        <Controller
                          control={control}
                          name="logo"
                          render={({ field }) => (
                            <InputUpload
                              {...field}
                              buttonName={t('PARTNER.UPLOAD_LOGO')}
                              clearErrors={clearErrors}
                              handleImageChange={(files) => setValue('logo', files)}
                              id="logo"
                              name="logo"
                              ref={field.ref}
                              title={t('PARTNER.UPLOAD_LOGO')}
                              value={getValues('logo')}
                            />
                          )}
                        />

                        {errors.logo && (
                          <TypographyCustomizer variant="caption">{errors.logo?.message}</TypographyCustomizer>
                        )}
                      </LabelField>
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField required recommend={t('PARTNER.SLIDER_RECOMMEND')} title={t('PARTNER.FAVICON')}>
                        <Controller
                          control={control}
                          name="favicon"
                          render={({ field }) => (
                            <InputUpload
                              {...field}
                              buttonName={t('PARTNER.UPLOAD_FAVICON')}
                              clearErrors={clearErrors}
                              handleImageChange={(files) => setValue('favicon', files)}
                              id="favicon"
                              name="favicon"
                              ref={field.ref}
                              title={t('PARTNER.UPLOAD_FAVICON')}
                              value={getValues('favicon')}
                            />
                          )}
                        />

                        {errors.favicon && (
                          <TypographyCustomizer variant="caption">{errors.favicon?.message}</TypographyCustomizer>
                        )}
                      </LabelField>
                    </Grid>

                    <Grid item xs={12}>
                      <LabelField required recommend={t('PARTNER.SLIDER_RECOMMEND')} title={t('PARTNER.SLIDER')}>
                        <Controller
                          control={control}
                          name="slider"
                          render={({ field }) => (
                            <InputUpload
                              multiple
                              {...field}
                              buttonName={t('PARTNER.UPLOAD_SLIDER')}
                              clearErrors={clearErrors}
                              handleImageChange={(files) => setValue('slider', files)}
                              id="slider"
                              name="slider"
                              ref={field.ref}
                              title={t('PARTNER.UPLOAD_SLIDER')}
                              value={getValues('slider')}
                            />
                          )}
                        />

                        {errors.slider && (
                          <TypographyCustomizer variant="caption">{errors.slider?.message}</TypographyCustomizer>
                        )}
                      </LabelField>
                    </Grid>
                  </Grid>
                </Grid>
              </Section>
              <Grid item xs={12}>
                <ButtonCustom type="submit" variant="contained">
                  {t('BUTTON.SAVE')}
                </ButtonCustom>
                <ButtonCustom
                  color="inherit"
                  component={InertiaLink}
                  href={route('admin.partners.index')}
                  variant="contained"
                >
                  {t('BUTTON.CANCEL')}
                </ButtonCustom>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </GridCustomizer>
  )
}
